import { effect, Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { environment } from '@env/environment';
import { NavigationEnd, Router } from '@angular/router';
import { ParticipantService } from '@shared/services/participant/participant.service';
import { ReleaseStage } from '@env/environment.model';

// @ts-ignore-next-line
declare const gtag: Gtag.Gtag;

export enum GaEvent {
  stepCompleted = 'step_completed',
}

interface CustomDimensions {
  user_id?: number;
  environment: ReleaseStage;
  company?: number;
  cohort?: string;
  full_url?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  constructor(
    private router: Router,
    @Inject(DOCUMENT) private document: any,
    private participantService: ParticipantService,
  ) {
    effect(() => {
      this.participantService.participant();
      this.trackParticipantIdChange(this.participantService.participant()?.id || null);
    });
  }

  public initialize(): void {
    // dynamically add analytics scripts to document head
    try {
      const gTagScript: HTMLScriptElement = document.createElement('script');
      gTagScript.async = true;
      gTagScript.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleAnalyticsId}`;
      document.head.appendChild(gTagScript);

      const dataLayerScript: HTMLScriptElement = document.createElement('script');
      dataLayerScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag( 'config',
              '${environment.googleAnalyticsId}',
              {
                'send_page_view': false,
                'environment': '${environment.releaseStage}',
              });`;
      document.head.appendChild(dataLayerScript);
    } catch (e) {
      console.error('Error adding Google Analytics', e);
    }

    this.trackPageView();
  }

  public trackParticipantIdChange(participantId: number | null): void {
    gtag('config', environment.googleAnalyticsId, {
      user_id: participantId,
      send_page_view: false,
      ...this.getCustomDimensions(),
    });
  }

  public sendEvent(eventName: GaEvent): void {
    gtag('event', eventName, this.getCustomDimensions());
  }

  public trackPageView(): void {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const pageLocation: string = this.document.location.href;
        gtag('event', 'page_view', {
          page_title: this.getPageTitleFromUrl(event.urlAfterRedirects),
          page_location: pageLocation,
          ...this.getCustomDimensions(),
        });
      }
    });
  }

  /**
   * @param url URL Path (eg: '/', '/potentialife-course/cycle-1/module-3/step-5-3/'
   */
  private getPageTitleFromUrl(url: string): string {
    if (url.startsWith(`/potentialife-course`)) {
      return url.substring(0, `/potentialife-course`.length);
    }

    return url;
  }

  private getCustomDimensions(): CustomDimensions {
    return {
      environment: environment.releaseStage,
      user_id: this.participantService.participant()?.id,
      company: this.participantService.participant()?.cohorts[0]?.company.id,
      cohort: this.participantService.participant()?.cohorts[0]?.code,
      full_url: this.router.url,
    };
  }
}
